import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, ChevronLeft, ChevronRight, Trash2, Loader2, BadgeCheck, ShieldCheck } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Loader } from "@/components/common/Loader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";

const LIMIT = 25;

export default function AdminUsers() {
    const { user: me } = useAuth();
    const [data, setData] = useState(null);
    const [search, setSearch] = useState("");
    const [skip, setSkip] = useState(0);
    const [active, setActive] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        const { data } = await api.get(`/admin/users?search=${encodeURIComponent(search)}&skip=${skip}&limit=${LIMIT}`);
        setData(data);
    }, [search, skip]);

    useEffect(() => { load(); }, [load]);

    const openUser = async (u) => {
        const { data } = await api.get(`/admin/users/${u.id}`);
        setActive(data);
    };

    const patch = async (changes) => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/admin/users/${active.id}`, changes);
            setActive({ ...active, ...data });
            toast.success("User updated.");
            load();
        } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Update failed."); }
        finally { setSaving(false); }
    };

    const remove = async () => {
        try {
            await api.delete(`/admin/users/${active.id}`);
            toast.success("User deleted.");
            setActive(null); load();
        } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Delete failed."); }
    };

    if (!data) return <Loader label="Loading users" />;
    const pages = Math.max(1, Math.ceil(data.total / LIMIT));
    const page = Math.floor(skip / LIMIT) + 1;

    return (
        <div className="ww-fade-up">
            <PageHeader title="User Management" subtitle={`${data.total} registered users`}>
                <div className="relative w-full sm:w-72">
                    <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input data-testid="user-search" value={search} onChange={(e) => { setSkip(0); setSearch(e.target.value); }}
                        placeholder="Search name or email..." className="bg-surface border-border rounded-sm h-10 pl-9" />
                </div>
            </PageHeader>

            <div className="bg-surface border border-border rounded-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead><tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                            <th className="text-left font-semibold px-4 py-3">User</th>
                            <th className="text-left font-semibold px-4 py-3">Plan</th>
                            <th className="text-left font-semibold px-4 py-3 hidden sm:table-cell">Role</th>
                            <th className="text-left font-semibold px-4 py-3 hidden md:table-cell">Verified</th>
                            <th className="text-right font-semibold px-4 py-3">Joined</th>
                        </tr></thead>
                        <tbody>
                            {data.users.map((u) => (
                                <tr key={u.id} onClick={() => openUser(u)} data-testid={`user-row-${u.id}`}
                                    className="border-b border-border/50 hover:bg-white/[0.03] cursor-pointer transition-colors">
                                    <td className="px-4 py-3"><div className="text-white">{u.name}</div><div className="text-xs text-muted-foreground font-mono">{u.email}</div></td>
                                    <td className="px-4 py-3"><StatusBadge status={u.plan === "pro" ? "Pro" : "Free"} /></td>
                                    <td className="px-4 py-3 hidden sm:table-cell">
                                        <span className={u.role === "admin" ? "text-success font-mono text-xs uppercase" : "text-muted-foreground capitalize"}>{u.role}</span>
                                    </td>
                                    <td className="px-4 py-3 hidden md:table-cell">
                                        {u.email_verified ? <BadgeCheck className="w-4 h-4 text-success" /> : <span className="text-xs text-muted-foreground">—</span>}
                                    </td>
                                    <td className="px-4 py-3 text-right text-muted-foreground font-mono text-xs">{String(u.created_at).slice(0, 10)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                    <span className="text-xs text-muted-foreground font-mono">Page {page} / {pages}</span>
                    <div className="flex gap-2">
                        <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - LIMIT))} data-testid="users-prev"
                            className="h-8 px-3 rounded-sm border border-border text-sm flex items-center gap-1 disabled:opacity-40 hover:bg-white/5"><ChevronLeft className="w-4 h-4" /> Prev</button>
                        <button disabled={skip + LIMIT >= data.total} onClick={() => setSkip(skip + LIMIT)} data-testid="users-next"
                            className="h-8 px-3 rounded-sm border border-border text-sm flex items-center gap-1 disabled:opacity-40 hover:bg-white/5">Next <ChevronRight className="w-4 h-4" /></button>
                    </div>
                </div>
            </div>

            <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
                <DialogContent className="bg-surface border-border max-w-lg">
                    <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-volt" /> {active?.name}</DialogTitle>
                        <DialogDescription className="font-mono text-xs">{active?.email}</DialogDescription>
                    </DialogHeader>
                    {active && (
                        <div className="space-y-5">
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {Object.entries(active.stats || {}).map(([k, v]) => (
                                    <div key={k} className="bg-obsidian border border-border rounded-sm p-2.5 text-center">
                                        <div className="font-mono text-white">{v}</div>
                                        <div className="text-[10px] uppercase text-muted-foreground tracking-wider">{k.replace("_", " ")}</div>
                                    </div>
                                ))}
                            </div>

                            <div className="space-y-3 border-t border-border pt-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-white">Plan</span>
                                    <Select value={active.plan} onValueChange={(v) => patch({ plan: v })}>
                                        <SelectTrigger className="w-[130px] h-9 bg-obsidian border-border rounded-sm" data-testid="user-plan-select"><SelectValue /></SelectTrigger>
                                        <SelectContent className="bg-surface2 border-border">
                                            <SelectItem value="free">Free</SelectItem>
                                            <SelectItem value="pro">Pro</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-white">Role</span>
                                    <Select value={active.role} onValueChange={(v) => patch({ role: v })} disabled={active.id === me?.id}>
                                        <SelectTrigger className="w-[130px] h-9 bg-obsidian border-border rounded-sm" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                                        <SelectContent className="bg-surface2 border-border">
                                            <SelectItem value="player">Player</SelectItem>
                                            <SelectItem value="admin">Admin</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-white">Email Verified</span>
                                    <Switch checked={active.email_verified} onCheckedChange={(v) => patch({ email_verified: v })} data-testid="user-verify-toggle" className="data-[state=checked]:bg-volt" />
                                </div>
                            </div>

                            <DialogFooter>
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <button disabled={active.id === me?.id} data-testid="user-delete-btn"
                                            className="h-9 px-4 rounded-sm border border-alert/50 text-alert hover:bg-alert/10 text-sm flex items-center gap-2 disabled:opacity-40">
                                            <Trash2 className="w-4 h-4" /> Delete User
                                        </button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent className="bg-surface2 border-border">
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Delete {active.name}?</AlertDialogTitle>
                                            <AlertDialogDescription>This permanently removes the user and all their matches, clips, reports and data. This cannot be undone.</AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel className="bg-transparent border-border">Cancel</AlertDialogCancel>
                                            <AlertDialogAction onClick={remove} data-testid="user-delete-confirm" className="bg-alert text-white hover:bg-[#ff4d7a]">Delete</AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                                {saving && <Loader2 className="w-4 h-4 animate-spin text-volt self-center" />}
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
